import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { MergeFieldChoices } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Banner, Button, useToast } from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { keepDuplicateSeparate, mergeDuplicate, undoMerge, useDuplicateCompare } from './api';
import type { CompareSide } from './types';
import styles from './MergePage.module.css';

type Choice = 'a' | 'b';
type Field = keyof MergeFieldChoices;

const CHOOSABLE: ReadonlySet<string> = new Set(['name', 'phone', 'email', 'lifecycle', 'owner']);

const STATUS_TEXT: Record<'merged' | 'kept_separate', string> = {
  merged: 'Diese Dublette wurde bereits zusammengeführt.',
  kept_separate: 'Diese Dublette wurde bereits entschieden: die Kontakte bleiben getrennt.',
};

function CompareCard({
  side,
  letter,
  choices,
  decidable,
  onChoose,
}: {
  side: CompareSide;
  letter: Choice;
  choices: Partial<MergeFieldChoices>;
  decidable: boolean;
  onChoose: (field: Field, choice: Choice) => void;
}) {
  return (
    <section className={clsx(styles.card, side.keeps && styles.cardKeeps)} aria-label={side.label}>
      <div className={styles.cardHead}>
        <span className={styles.eyebrow}>{side.label}</span>
        {side.keeps ? <span className={styles.keeps}>bleibt</span> : null}
      </div>
      <div className={styles.rows} role="group" aria-label={`Werte aus ${side.label}`}>
        {side.rows.map((row) => {
          const choosable = CHOOSABLE.has(row.key) && decidable;
          const chosen = CHOOSABLE.has(row.key) ? choices[row.key as Field] === letter : side.keeps;
          return (
            <button
              key={row.key}
              type="button"
              className={styles.row}
              role={CHOOSABLE.has(row.key) ? 'radio' : undefined}
              aria-checked={CHOOSABLE.has(row.key) ? chosen : undefined}
              disabled={!choosable}
              title={CHOOSABLE.has(row.key) ? undefined : 'wird vollständig in Kontakt A übernommen'}
              onClick={() => (choosable ? onChoose(row.key as Field, letter) : undefined)}
            >
              <span className={styles.rowLabel}>{row.label}</span>
              <span className={styles.rowValue}>{row.value}</span>
              <span
                className={clsx(
                  styles.radio,
                  chosen && styles.radioOn,
                  !CHOOSABLE.has(row.key) && styles.radioFixed,
                )}
                aria-hidden="true"
              />
            </button>
          );
        })}
      </div>
    </section>
  );
}

/** Kontakte 08 · „Möglicher Doppeleintrag": value per field, keep separate or merge (`/tasks/duplicates/:candidateId`). */
export function MergePage() {
  const { candidateId = '' } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [choices, setChoices] = useState<Partial<MergeFieldChoices>>({});
  const compare = useDuplicateCompare(candidateId, choices);
  const [pending, setPending] = useState<'merge' | 'keep' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const data = compare.data;
  // The server's defaults plus the radios the user changed (each change re-fetches the recomputed `info`).
  const effective: Partial<MergeFieldChoices> = { ...data?.choices, ...choices };

  async function keepSeparate() {
    setPending('keep');
    setError(null);
    try {
      await keepDuplicateSeparate(candidateId);
      toast({ message: 'Dublette entschieden: die Kontakte bleiben getrennt.' });
      await navigate('/tasks/approvals');
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(null);
    }
  }

  async function merge() {
    if (!data) return;
    setPending('merge');
    setError(null);
    try {
      const result = await mergeDuplicate(candidateId, { ...data.choices, ...choices });
      toast({
        message: 'Zusammengeführt. Rückgängig machen geht 30 Tage lang über den Verlauf.',
        duration: 30_000,
        action: {
          label: 'Rückgängig',
          onClick: () => {
            undoMerge(result.mergeId)
              .then(() => {
                toast({ message: 'Zusammenführung rückgängig gemacht.' });
                return navigate(`/tasks/duplicates/${candidateId}`);
              })
              .catch((cause: unknown) => toast({ message: errorMessage(cause) }));
          },
        },
      });
      await navigate(`/contacts/${result.targetId}`);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : errorMessage(cause));
    } finally {
      setPending(null);
    }
  }

  const decidable = data?.status === 'open' && data.canDecide;

  return (
    <div className={styles.page}>
      <div className={styles.headRow}>
        <div className={styles.headText}>
          <div className={styles.crumb}>
            <button type="button" className={styles.crumbLink} onClick={() => void navigate('/tasks')}>
              Tasks
            </button>
            {' / '}
            <button
              type="button"
              className={styles.crumbLink}
              onClick={() => void navigate('/tasks/approvals')}
            >
              Freigaben
            </button>
          </div>
          <h1 className={styles.title}>{data?.title ?? 'Möglicher Doppeleintrag'}</h1>
          <div className={styles.meta}>{data?.meta ?? (compare.isPending ? 'wird geladen' : '')}</div>
        </div>
        <div className={styles.actions}>
          <Button
            variant="secondary"
            disabled={!decidable || pending !== null}
            loading={pending === 'keep'}
            onClick={() => void keepSeparate()}
          >
            Getrennt lassen
          </Button>
          <Button
            disabled={!decidable || !data?.canMerge || pending !== null}
            loading={pending === 'merge'}
            onClick={() => void merge()}
          >
            Zusammenführen
          </Button>
        </div>
      </div>

      {compare.isError ? (
        <Banner
          tone="danger"
          title="Die Dublette konnte nicht geladen werden."
          actions={
            <Button variant="secondary" size="small" onClick={() => void compare.refetch()}>
              Erneut versuchen
            </Button>
          }
        >
          {errorMessage(compare.error)}
        </Banner>
      ) : null}
      {data && data.status !== 'open' ? <Banner tone="neutral">{STATUS_TEXT[data.status]}</Banner> : null}
      {data && data.status === 'open' && !data.canDecide ? (
        <Banner tone="info">
          Nur die empfangende Person oder ein Admin entscheidet über diese Dublette.
        </Banner>
      ) : null}
      {data && data.status === 'open' && data.canDecide && !data.canMerge ? (
        <Banner tone="info">
          Zusammenführen ist Admins vorbehalten — „Getrennt lassen“ können Sie entscheiden.
        </Banner>
      ) : null}
      {error ? <Banner tone="danger">{error}</Banner> : null}

      <div className={styles.grid}>
        {data ? (
          <>
            <CompareCard
              side={data.a}
              letter="a"
              choices={effective}
              decidable={Boolean(decidable && data.canMerge)}
              onChoose={(field, choice) => setChoices((current) => ({ ...current, [field]: choice }))}
            />
            <CompareCard
              side={data.b}
              letter="b"
              choices={effective}
              decidable={Boolean(decidable && data.canMerge)}
              onChoose={(field, choice) => setChoices((current) => ({ ...current, [field]: choice }))}
            />
          </>
        ) : compare.isPending ? (
          <>
            <div className={styles.skeletonCard} aria-hidden="true" />
            <div className={styles.skeletonCard} aria-hidden="true" />
          </>
        ) : null}
      </div>
      {data ? <div className={styles.info}>{data.info}</div> : null}
    </div>
  );
}
